import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, Send, ExternalLink, Plus, Trash2, FileText, Users, AlertCircle, Cake, CalendarClock, Zap } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const SEGMENTS = [
  { value: "all", label: "Todos os alunos ativos", icon: Users, color: "text-slate-600" },
  { value: "expiring_7d", label: "Contrato vence em até 7 dias", icon: CalendarClock, color: "text-amber-600" },
  { value: "expiring_3d", label: "Contrato vence em até 3 dias", icon: CalendarClock, color: "text-orange-600" },
  { value: "overdue", label: "Inadimplentes / vencidos", icon: AlertCircle, color: "text-rose-600" },
  { value: "birthday_today", label: "Aniversariantes de hoje", icon: Cake, color: "text-pink-600" },
  { value: "inactive_7d", label: "Sem frequentar há 7+ dias", icon: Zap, color: "text-amber-600" },
  { value: "inactive_30d", label: "Sem frequentar há 30+ dias", icon: Zap, color: "text-rose-600" },
  { value: "paid", label: "Pagamento em dia", icon: Users, color: "text-emerald-600" },
  { value: "pending", label: "Pagamento pendente", icon: AlertCircle, color: "text-amber-600" },
];

const DEFAULT_TEMPLATES = [
  { name: "Aviso de vencimento (7 dias)", category: "vencimento", content: "Olá {nome}! Seu plano {plano} vence em {dias} dias ({vencimento}). Renove com 1 clique aqui 👉 {link_pagar} Valor: {valor}. — {academia}" },
  { name: "Contrato vencendo (3 dias)", category: "vencimento", content: "Oi {nome}, faltam só {dias} dias para o vencimento do seu plano {plano} ({vencimento}). Renove agora e não perca acesso: {link_pagar} 💪 — {academia}" },
  { name: "Cobrança de inadimplente", category: "cobranca", content: "{nome}, seu plano venceu em {vencimento}. Acesso à catraca já bloqueado. Renove em segundos aqui: {link_pagar} — {academia}" },
  { name: "Feliz aniversário 🎂", category: "relacionamento", content: "🎉 Parabéns, {nome}! Feliz aniversário! Um presentinho seu no balcão essa semana 👀 — {academia}" },
  { name: "Sentimos sua falta", category: "retencao", content: "Oi {nome}! Notei que você não passa aqui há um tempo. Tudo bem? Bora voltar aos treinos — te espero essa semana! — {academia}" },
];

export default function CRM() {
  const [tab, setTab] = useState("campaign");
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [segment, setSegment] = useState("expiring_7d");
  const [selectedTpl, setSelectedTpl] = useState("");
  const [freeContent, setFreeContent] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  // Template dialog
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", content: "", category: "vencimento" });
  const [editingTpl, setEditingTpl] = useState(null);

  const load = async () => {
    const [t, h] = await Promise.all([api.get("/crm/templates"), api.get("/crm/campaign/history?limit=20")]);
    setTemplates(t.data); setHistory(h.data);
    // Seed default templates if empty
    if (t.data.length === 0) {
      for (const dt of DEFAULT_TEMPLATES) {
        try { await api.post("/crm/templates", dt); } catch {}
      }
      const t2 = await api.get("/crm/templates");
      setTemplates(t2.data);
    }
  };
  useEffect(() => { load(); }, []);

  const currentContent = () => {
    if (selectedTpl) return templates.find(t => t.id === selectedTpl)?.content || "";
    return freeContent;
  };

  const generatePreview = async () => {
    setLoading(true);
    try {
      const body = selectedTpl ? { template_id: selectedTpl, segment } : { content: freeContent, segment };
      const { data } = await api.post("/crm/campaign/preview", body);
      setPreview(data);
      if (data.count === 0) toast.info(`${data.matched_students} aluno(s) no segmento, mas nenhum tem telefone válido.`);
      else toast.success(`${data.count} contatos preparados`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const openAll = async () => {
    if (!preview || preview.count === 0) return;
    if (!window.confirm(`Abrir ${preview.count} janelas do WhatsApp?`)) return;
    preview.recipients.forEach((r, i) => setTimeout(() => window.open(r.wa_link, "_blank"), i * 400));
    try {
      await api.post("/crm/campaign/log", {
        template_id: selectedTpl || null, segment, count: preview.count, message: currentContent(),
      });
      load();
    } catch {}
  };

  const saveTemplate = async () => {
    if (!tplForm.name || !tplForm.content) return toast.error("Preencha nome e mensagem");
    try {
      if (editingTpl) await api.patch(`/crm/templates/${editingTpl.id}`, tplForm);
      else await api.post("/crm/templates", tplForm);
      toast.success("Salvo");
      setTplOpen(false); setEditingTpl(null); setTplForm({ name: "", content: "", category: "vencimento" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const editTpl = (t) => { setEditingTpl(t); setTplForm({ name: t.name, content: t.content, category: t.category }); setTplOpen(true); };
  const removeTpl = async (id) => {
    if (!window.confirm("Remover template?")) return;
    await api.delete(`/crm/templates/${id}`); toast.success("Removido"); load();
  };

  const insertVar = (v) => {
    setTplForm(prev => ({ ...prev, content: prev.content + " " + v }));
  };

  const segMeta = SEGMENTS.find(s => s.value === segment);

  return (
    <div className="space-y-4">
      <PageHeader
        title="CRM WhatsApp"
        subtitle="Segmente alunos, personalize a mensagem e envie em massa via wa.me"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="campaign" data-testid="tab-crm-campaign">Nova campanha</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-crm-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-crm-history">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="campaign" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 border border-slate-200">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2 mb-2">
                <Users className="w-4 h-4"/>1. Escolha o segmento
              </Label>
              <Select value={segment} onValueChange={setSegment}>
                <SelectTrigger data-testid="crm-segment-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2 mb-2 mt-6">
                <FileText className="w-4 h-4"/>2. Use um template (ou escreva livre)
              </Label>
              <Select value={selectedTpl || "__free__"} onValueChange={(v)=>setSelectedTpl(v === "__free__" ? "" : v)}>
                <SelectTrigger data-testid="crm-template-select"><SelectValue placeholder="Selecionar template"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__free__">— Escrever mensagem livre —</SelectItem>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>

              {!selectedTpl && (
                <Textarea rows={6} className="mt-3" placeholder="Olá {nome}, tudo bem?..."
                  value={freeContent} onChange={(e)=>setFreeContent(e.target.value)}
                  data-testid="crm-free-content"/>
              )}
              {selectedTpl && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm border border-slate-200 whitespace-pre-line">{templates.find(t=>t.id===selectedTpl)?.content}</div>
              )}
              <p className="text-xs text-slate-500 mt-2">
                Variáveis: <code>{"{nome} {vencimento} {dias} {plano} {valor} {academia} {link_pagar}"}</code>
              </p>

              <Button onClick={generatePreview} disabled={loading}
                data-testid="crm-generate-button"
                className="mt-4 w-full bg-rose-600 hover:bg-rose-700">
                <MessageCircle className="w-4 h-4 mr-2"/>{loading ? "Gerando..." : "Gerar prévia"}
              </Button>
            </Card>

            <Card className="p-5 border border-slate-200 bg-emerald-50/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600"/>
                  {preview ? `${preview.count} destinatários` : "Prévia"}
                </h3>
                {segMeta && <span className={`text-xs font-semibold ${segMeta.color}`}>{segMeta.label}</span>}
              </div>
              {!preview && <p className="text-sm text-slate-500 py-8 text-center">Gere a prévia para ver os alunos que receberão a mensagem.</p>}
              {preview && preview.count === 0 && (
                <div className="py-6 text-center">
                  <p className="text-sm text-slate-500">Nenhum contato encontrado para este segmento (verificar telefones cadastrados).</p>
                </div>
              )}
              {preview && preview.count > 0 && (
                <>
                  <Button onClick={openAll} data-testid="crm-open-all-button"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 mb-3">
                    <ExternalLink className="w-4 h-4 mr-2"/>Abrir os {preview.count} no WhatsApp
                  </Button>
                  <div className="max-h-96 overflow-y-auto space-y-2" data-testid="crm-preview-list">
                    {preview.recipients.map(r => (
                      <div key={r.student_id} className="p-2 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <div className="text-sm font-medium text-slate-900">{r.name}</div>
                            <div className="text-xs text-slate-500 font-mono">+{r.phone}</div>
                          </div>
                          <a href={r.wa_link} target="_blank" rel="noreferrer"
                            className="text-xs bg-emerald-600 text-white px-3 py-1 rounded-full font-semibold hover:bg-emerald-700 shrink-0">
                            Enviar →
                          </a>
                        </div>
                        <div className="text-xs text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-line">{r.message}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={tplOpen} onOpenChange={(v)=>{setTplOpen(v); if(!v){setEditingTpl(null); setTplForm({name:"",content:"",category:"vencimento"});}}}>
              <DialogTrigger asChild>
                <Button data-testid="crm-template-new-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2"/>Novo template</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editingTpl ? "Editar" : "Novo"} template</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-4">
                  <div><Label>Nome</Label><Input value={tplForm.name} onChange={(e)=>setTplForm({...tplForm,name:e.target.value})} data-testid="crm-template-name-input"/></div>
                  <div><Label>Categoria</Label><Input value={tplForm.category} onChange={(e)=>setTplForm({...tplForm,category:e.target.value})} placeholder="vencimento, cobranca, relacionamento..."/></div>
                  <div>
                    <Label>Mensagem</Label>
                    <Textarea rows={6} value={tplForm.content} onChange={(e)=>setTplForm({...tplForm,content:e.target.value})} data-testid="crm-template-content-input"/>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["{nome}", "{vencimento}", "{dias}", "{plano}", "{valor}", "{academia}", "{link_pagar}"].map(v => (
                      <button key={v} onClick={()=>insertVar(v)}
                        className="text-xs px-2 py-1 rounded-full bg-slate-100 hover:bg-rose-100 text-slate-700 font-mono">
                        {v}
                      </button>
                    ))}
                  </div>
                  <Button onClick={saveTemplate} data-testid="crm-template-save-button" className="w-full bg-rose-600 hover:bg-rose-700">Salvar</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-2 gap-3" data-testid="crm-templates-list">
            {templates.map(t => (
              <Card key={t.id} className="p-4 border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-bold text-slate-900">{t.name}</h4>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t.category}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={()=>editTpl(t)}>Editar</Button>
                    <Button variant="ghost" size="icon" onClick={()=>removeTpl(t.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-line line-clamp-4">{t.content}</p>
              </Card>
            ))}
            {templates.length === 0 && <p className="col-span-full text-center text-slate-500 py-8">Nenhum template ainda</p>}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Data</th><th className="px-4 py-3">Segmento</th>
                  <th className="px-4 py-3 text-right">Enviadas</th><th className="px-4 py-3">Operador</th>
                  <th className="px-4 py-3">Mensagem</th>
                </tr>
              </thead>
              <tbody data-testid="crm-history-list">
                {history.map(h => (
                  <tr key={h.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 text-sm">{new Date(h.created_at).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-3 text-sm">{SEGMENTS.find(s => s.value === h.segment)?.label || h.segment}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">{h.count}</td>
                    <td className="px-4 py-3 text-sm">{h.sent_by_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{h.message}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">Nenhuma campanha enviada</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
